// Signing in, the couch's saved players, and saved games.
//
// Anyone can sign in with Google (Matt, 2026-10-01: "anyone should be able
// to sign into this one" - no approval gate, unlike the other hub games).
// One sign-in serves a whole couch: the account keeps a list of the people
// who play at this computer, each with a name and a favourite colour, and
// each seat picks one of them with its own controller.
//
//   users/<uid>            { people: { <pid>: { name, colour, order } } }
//   games/<id>             one finished game, written by the host's computer
//   userGames/<uid>/<id>   the games an account was in (its own index)
//
// An anonymous session (the silent one the bug button makes) is not
// "signed in" here.

import {
  ref as dbRef, get, set, update, push, remove, serverTimestamp,
} from 'firebase/database';
import {
  GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut as fbSignOut, signInAnonymously,
  signInWithCustomToken, updateProfile,
} from 'firebase/auth';
import { db, auth } from '../firebase';
import { freshColour } from './palette.js';

export const accountsAvailable = () => Boolean(db && auth);

const isReal = (user) => Boolean(user && !user.isAnonymous);

export function watchAccount(callback) {
  if (!auth) return () => {};
  return onAuthStateChanged(auth, (user) => callback(isReal(user) ? user : null));
}

export async function signIn() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(auth, provider);
}

export async function signOut() {
  await fbSignOut(auth);
}

const firstName = (user) => (user.displayName || 'Player').trim().split(/\s+/)[0].slice(0, 16);

/**
 * The account's couch players, as a sorted array [{ pid, name, colour }].
 * A brand-new account starts with one player: its owner.
 */
export async function loadPeople(user) {
  const snap = await get(dbRef(db, `users/${user.uid}/people`));
  let people = snap.val();
  if (!people) {
    people = { me: { name: firstName(user), colour: 'coral', order: 0 } };
    await set(dbRef(db, `users/${user.uid}/people`), people);
  }
  return Object.entries(people)
    .map(([pid, p]) => ({ pid, name: p.name, colour: p.colour, order: p.order || 0 }))
    .sort((a, b) => a.order - b.order);
}

export async function savePerson(uid, person) {
  await update(dbRef(db, `users/${uid}/people/${person.pid}`), {
    name: person.name, colour: person.colour, order: person.order,
  });
}

export async function addPerson(uid, people) {
  const pid = push(dbRef(db, `users/${uid}/people`)).key;
  const person = {
    pid,
    name: `Player ${people.length + 1}`,
    colour: freshColour(people.map((p) => p.colour)),
    order: Math.max(0, ...people.map((p) => p.order)) + 1,
  };
  await savePerson(uid, person);
  return person;
}

export async function removePerson(uid, pid) {
  await remove(dbRef(db, `users/${uid}/people/${pid}`));
}

/**
 * Saves a finished game. Called on the host's computer; returns its id so
 * guests' computers can add it to their own accounts' lists.
 */
export async function recordGame(record) {
  if (!db || !auth) return null;
  if (!auth.currentUser) await signInAnonymously(auth);
  const uid = auth.currentUser.uid;
  const ref = push(dbRef(db, 'games'));
  await set(ref, { ...record, host: uid, at: serverTimestamp() });
  if (isReal(auth.currentUser)) await set(dbRef(db, `userGames/${uid}/${ref.key}`), true);
  return ref.key;
}

export async function claimGame(id) {
  const user = auth?.currentUser;
  if (!isReal(user) || !id) return;
  await set(dbRef(db, `userGames/${user.uid}/${id}`), true);
}

// Every game this account was in, newest first.
export async function loadGames(uid) {
  const index = (await get(dbRef(db, `userGames/${uid}`))).val() || {};
  const ids = Object.keys(index);
  const games = await Promise.all(ids.map(async (id) => {
    const v = (await get(dbRef(db, `games/${id}`))).val();
    return v ? { id, ...v } : null;
  }));
  return games.filter(Boolean).sort((a, b) => (b.at || 0) - (a.at || 0));
}

// Browser checks can't drive Google's popup, so on the dev server only, a
// test can sign in with a token minted by scripts/mint-test-token.mjs.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__signInWithToken = async (token, displayName) => {
    const { user } = await signInWithCustomToken(auth, token);
    if (displayName) await updateProfile(user, { displayName });
    return user.uid;
  };
}
